import type { ButtonState, IconModel, ImageModel, LinkModel, PaginationModel } from '@flocasts/experience-service-types'
import type { Params, UrlTree } from '@angular/router'
import { UrlTreeCreator } from 'src/app/live/services/routing.utility'
import { FacetsObject } from 'src/app/shared/facets/models'
import {
  SearchPreFacetedTypes,
  SearchContentType,
  SearchMeta
} from 'src/app/singleton-services/search-data.service.interface'
import { SearchTab } from './old-search.interfaces'

/**
 * TODO: When search is moved to BFF, these methods can be removed
 *
 * These are methods to convert Core API data into BFF models for the Search Page
 */

export const mapSearchExtraToTabs = (
  extra: [{ facets: FacetsObject }, SearchPreFacetedTypes],
  typeParam: string | undefined
): ReadonlyArray<SearchTab> => {
  const tabList = [
    {
      value: '',
      text: 'All',
      active: !typeParam
    }
  ]

  extra.map(e => {
    if ('pre_faceted_display_names' in e) {
      e.pre_faceted_types.map(tab => {
        // Omit Leagues and Associations until hockey tech integration is complete.
        if (tab.value !== 'supporter' && tab.value !== 'league' && tab.value !== 'association') {
          tabList.push({
            value: tab.value,
            text: e.pre_faceted_display_names[tab.value],
            active: typeParam === tab.value
          })
        }
      })
    }
  })

  return tabList
}

// Used for search. Excludes categories and supporter
export const isSearchContentType = (type: string | undefined): type is SearchContentType => {
  return (
    type === 'article' ||
    type === 'ranking' ||
    type === 'video' ||
    type === 'event' ||
    type === 'result' ||
    type === 'person' ||
    type === 'team' ||
    type === 'collection' ||
    type === 'program'
  )
}

/**
 * Display a relevant date for the content type
 *
 * Event, single date = [event_start_date]
 * Videos = [last_updated_date]
 * Articles = [last_updated_date]
 * Rankings = [last_updated_date]
 * Results = [last_updated_date]
 * Athletes = do not display a date
 * Teams = do not display a date
 * Collections = [last_updated_date]
 */
// TODO add Event, range of dates = [event_start_date] - [event_end_date]
export const mapSearchItemToDateRange = (
  type: SearchContentType,
  publishStartDate: string,
  eventStartDate: string | undefined
): string => {
  if (type === 'person' || type === 'team' || type === 'program') return ''
  if (type === 'event' && eventStartDate) {
    return eventStartDate
  }
  return publishStartDate
}

// Pagination

/**
 * Creates the PaginationModel from Core API SearchMeta
 */
export const mapSearchMetaToPaginationModel = (
  meta: SearchMeta,
  router: UrlTreeCreator,
  queryParams: Params
): PaginationModel => {
  let links: LinkModel[] = []
  let title = ''

  // check for bad metadata
  if (meta.pages > 0 && meta.page > 0) {
    title = buildSearchPaginationTitle(meta)
    const [firstPage, lastPageInBar] = getPaginationRange(meta.page, meta.pages)
    const paginationNumbers = buildLinkArray(firstPage, lastPageInBar, meta.page, router, queryParams)
    const forwardButton = buildSearchButtonPagination(
      meta.page,
      {
        id: 'non-unique-id',
        type: 'icon',
        text: null,
        style: 'icon-angle-right',
        accessibility: {
          type: 'accessibility',
          altText: 'Forward'
        }
      },
      meta.page === meta.pages ? 'disabled' : 'unselected',
      meta.page === meta.pages ? null : createSearchUrlTree(router)({ ...queryParams, page: meta.page + 1 }).toString()
    )

    const backButton = buildSearchButtonPagination(
      meta.page,
      {
        id: 'non-unique-id',
        type: 'icon',
        text: null,
        style: 'icon-angle-left',
        accessibility: {
          type: 'accessibility',
          altText: 'Backward'
        }
      },
      meta.page === 1 ? 'disabled' : 'unselected',
      meta.page === 1 ? null : createSearchUrlTree(router)({ ...queryParams, page: meta.page - 1 }).toString()
    )

    links = [backButton, ...paginationNumbers, forwardButton]
  }

  return {
    id: 'search-pagination',
    style: 'standard',
    title,
    type: 'pagination',
    links
  }
}

/**
 * Builds the pagination title
 * ex: '1-50 of 75'
 */
const buildSearchPaginationTitle = (meta: SearchMeta) => {
  return `${meta.offset ? meta.offset.toLocaleString() : 1}-${Math.min(
    meta.offset + meta.limit,
    meta.total
  ).toLocaleString()} of ${meta.total.toLocaleString()}`
}

/** Builds a basic LinkModel from Core API SearchMeta */
const buildLinkModel = (page: number | null, selectedPage: number, url: string | null): LinkModel => {
  return {
    title: page ? page.toString() : '',
    type: 'link',
    url,
    style: 'pagination-button',
    state: page === selectedPage ? 'selected' : 'unselected',
    shareUrl: null,
    nodeId: null,
    nodeType: null,
    nextStyle: null,
    id: 'non-unique-id',
    contentMetadata: null,
    apiCall: null,
    asset: null,
    secondaryAsset: null,
    mergeParams: null,
    screenType: 'unknown',
    analytics: {
      type: 'analytics',
      siteId: null,
      siteName: null,
      nodeId: null,
      nodeType: null,
      primaryAssociationId: null,
      primaryAssociationTitle: null,
      primaryAssociationShortTitle: null,
      name: null,
      collectionSlug: null,
      slugUri: null,
      entityId: null,
      entityType: null,
      isPremium: false,
      extraContext: {},
      videoAnalytics: null
    }
  }
}

/** Builds an url for the LinkModel */
const createSearchUrlTree =
  (router: UrlTreeCreator) =>
  (queryParams: Params): UrlTree => {
    return router.createUrlTree(['search/'], { queryParams: { ...queryParams } })
  }

/**
 * Builds the forward/backward buttons for pagination
 */
export const buildSearchButtonPagination = (
  selectedPage: number,
  asset: ImageModel | IconModel | null,
  state: ButtonState | null,
  url: string | null
): LinkModel => {
  const link = buildLinkModel(null, selectedPage, null)
  return {
    ...link,
    url,
    asset,
    state
  }
}

/**
 * Builds the numeric pagination buttons
 * ex: [1] [2] [3] [4] [5]
 */
const buildLinkArray = (
  firstPage: number,
  lastPage: number,
  selectedPage: number,
  router: UrlTreeCreator,
  queryParams: Params
): LinkModel[] => {
  const linkArray: LinkModel[] = []

  if (firstPage > 0) {
    for (let i = firstPage; i <= lastPage; i++) {
      const url = createSearchUrlTree(router)({ ...queryParams, page: i }).toString()
      linkArray.push(buildLinkModel(i, selectedPage, url))
    }
  }

  return linkArray
}

/**
 * Gets the first and last pages to paginate on
 * Borrowed from BFF
 */
function getPaginationRange(selectedPage: number, lastPage: number, pages = 5): [firstPage: number, lastPage: number] {
  const firstPage = selectedPage <= 5 ? 1 : Math.floor(selectedPage / pages) * pages + 1
  const lastPageInBar = Math.min(firstPage + pages - 1, lastPage)

  return [firstPage, lastPageInBar]
}
